import { Struct, Value } from "@bufbuild/protobuf";
import { type Getter, type Setter, atom } from "jotai";
import { Node } from "prosemirror-model";

import {
	generateShortUuid,
	getDefaultHttpRequestPage,
	parsePage,
	serializePage,
} from "@moment/api-collab";
import { type HttpRequestPage, type PageMetadata } from "@moment/api-collab/api-types";
import { Logger } from "@moment/logging";
import { schema } from "@moment/prosemirror-schema";

import {
	$document,
	docsListCollabEntry,
	docsListItemLocalEntry,
	docsListItemRemoteEntry,
} from "../document-list";
import { metadataKeyForDocsRoute } from "../document-metadata/documentMetadataKey";
import { $documentMetadata } from "../document-metadata/metadata-state.atom";
import { $isDocumentPublished } from "../document/sharing/permissions.atom";
import { env } from "../env";
import { fs } from "../filesystem";
import { buildDefaultPagePath, sluggify } from "../page-metadata/page-metadata.atom";
import { generateUniquePageTitle } from "../page-metadata/page-title.utils";
import { getPageDirPath, paths } from "../paths";
import { $collabRegistryConnection } from "../reboot/collab-registry-connection.atom";
import { serializeForStruct } from "../reboot/utils/editor-state-utils";
import { $loadHttpRequestPage } from "../request-page/request-page.atom";
import { requestPageKeyForDocsRoute } from "../request-page/requestPageKey";
import {
	CreatePageCollaDocumentMetadataError,
	CreatePageCollabNoRegistryConnectionError,
	CreatePageDirCollisionError,
	CreatePageDocumentNotFoundError,
	CreatePageUnsupportedPageTypeError,
} from "./errors";
import { EMPTY_DOC_JSON } from "./page.types";
import { getCollisionFallbackDirRelative } from "./rename-page-dir.utils";

const logger = new Logger("createPage");

async function resolveUniqueSlugPageDir(
	get: Getter,
	fsApi: ReturnType<typeof fs>,
	documentPath: string,
	pageId: string,
	slug: string
): Promise<{ dirName: string; pageDirPath: string }> {
	const targetDirRelative = `pages/${slug}`;
	let pageDirPath = `${documentPath}/${targetDirRelative}`;

	const targetExists = await get(fsApi.exists({ filePath: pageDirPath }));
	if (!targetExists) {
		return { dirName: slug, pageDirPath };
	}

	const fallbackDirRelative = getCollisionFallbackDirRelative(pageId, slug);
	pageDirPath = `${documentPath}/${fallbackDirRelative}`;

	const fallbackExists = await get(fsApi.exists({ filePath: pageDirPath }));
	if (fallbackExists) {
		logger.warn("Both page directory targets exist, aborting create", {
			targetDirRelative,
			fallbackDirRelative,
		});
		throw new CreatePageDirCollisionError(targetDirRelative, fallbackDirRelative);
	}

	return {
		dirName: fallbackDirRelative.replace("pages/", ""),
		pageDirPath,
	};
}

const buildPageMetadata = ({
	pageId,
	pageType,
	pageMetadata,
}: {
	pageId: string;
	pageType: PageMetadata["type"];
	pageMetadata?: Partial<PageMetadata>;
}): PageMetadata => ({
	id: pageId,
	title: "",
	slug: pageMetadata?.slug ?? pageId,
	archived: false,
	parentPageID: null,
	type: pageType,
	...pageMetadata,
});

export type CreatePageInput = {
	documentId: string;
	pageMetadata?: Partial<PageMetadata>;
	pageIndex?: number;
	pageType: "markdown" | "canvas" | "request";
	initialPageContents?: string;
	/** Optional body type for initial page contents. Defaults to "markdown". */
	bodyType?: "markdown";
	/** Optional initial content for HTTP request pages. If not provided, uses the default request page. */
	initialRequestPageContents?: HttpRequestPage;
	/** Optional explicit storage path relative to document root (e.g., "pages/2026/01/21").
	 *  If not provided, defaults to "pages/{pageId}". */
	storagePath?: string;
	/** Optional path to the page file relative to document root (e.g., "pages/2026/01/21/index.md").
	 *  Stored in page metadata so loading/saving uses the correct location. */
	path?: string;
};

export type CreatePageSortedInput = CreatePageInput & {
	/** Comparator function to determine the sorted position of the new page */
	comparator: (a: PageMetadata, b: PageMetadata) => number;
};

/**
 * Shared helper for creating pages with different metadata insertion strategies.
 *
 * VERY VERY IMPORTANT:
 *
 * Collab server updates the metadata store transactionally. We MUST NOT try to update the
 * metadata store ourselves, we need to WAIT for it to be provided to us via tableChanges. If
 * you mess this up you'll get weird errors like `Record metadata already exists`, which happens
 * because we apply some transactions, and then the server tries to apply more-or-less the same
 * transactions later.
 *
 * If you do change this, ensure the following:
 *
 *   - Altering document metadata works fine. Change the title etc.
 *   - Creating a page works fine.
 */
async function createPageWithStrategy(
	get: Getter,
	set: Setter,
	input: CreatePageInput,
	insertPage: (existingPages: PageMetadata[], newPage: PageMetadata) => PageMetadata[]
): Promise<PageMetadata> {
	const enabled = !env.isSsr && env.isDesktopApp;

	if (!enabled) {
		logger.error("No filesystem available");
		throw new Error("No filesystem available");
	}

	logger.debug("Starting create page workflow", { input });

	// Check for duplicate pages by ID or slug before creating
	// This protects against race conditions (e.g., React Strict Mode double-renders)
	if (input.pageMetadata?.id || input.pageMetadata?.slug) {
		const metadataKey = metadataKeyForDocsRoute(input.documentId);
		const currentMetadata = get($documentMetadata(metadataKey));
		const existingPages = currentMetadata?.pagesMetadata ?? [];

		const pageIdExists = input.pageMetadata?.id
			? existingPages.find((p) => p.id === input.pageMetadata!.id)
			: undefined;

		if (pageIdExists) {
			logger.debug("Page with same ID already exists, returning existing page", {
				documentId: input.documentId,
				pageId: input.pageMetadata.id,
			});
			return pageIdExists;
		}
	}

	const { documentId } = input;
	const metadataKey = metadataKeyForDocsRoute(documentId);
	const currentMetadata = get($documentMetadata(metadataKey));
	const existingPages = currentMetadata?.pagesMetadata ?? [];
	const normalizedPageMetadata =
		input.pageMetadata === undefined
			? undefined
			: {
					...input.pageMetadata,
					title: generateUniquePageTitle(
						input.pageMetadata.title ?? "",
						input.pageMetadata.id,
						existingPages
					),
				};

	const { pageMetadata, collabUsed } = await (async () => {
		switch (input.pageType) {
			case "markdown": {
				return await createMarkdownPage(get, set, {
					...input,
					pageMetadata: normalizedPageMetadata,
				});
			}
			case "canvas":
				return await createEmptyCanvasPage(get, set, {
					...input,
					pageMetadata: normalizedPageMetadata,
				});
			case "request":
				return await createRequestPage(get, set, {
					...input,
					pageMetadata: normalizedPageMetadata,
				});
			default:
				throw new CreatePageUnsupportedPageTypeError(input.pageType);
		}
	})();

	// For local-only documents, update metadata using the provided insertion strategy
	if (!collabUsed) {
		const latestMetadata = get($documentMetadata(metadataKey));
		const latestPages = latestMetadata?.pagesMetadata ?? [];
		const pagesMetadata = insertPage(latestPages, pageMetadata);

		await set($documentMetadata(metadataKey), { pagesMetadata });

		logger.debug("Document metadata updated with new page", { metadataKey, pagesMetadata });
	}

	return pageMetadata;
}

export const $createPage = atom(null, async (get, set, input: CreatePageInput) => {
	return createPageWithStrategy(get, set, input, (existingPages, newPage) => {
		if (input.pageIndex !== undefined && input.pageIndex < existingPages.length) {
			const copy = [...existingPages];
			copy.splice(input.pageIndex, 0, newPage);
			return copy;
		}
		return [...existingPages, newPage];
	});
});

/**
 * Creates a page and inserts it in sorted order based on the provided comparator.
 * This is useful for daily notes where pages should be sorted by date (newest first).
 */
export const $createPageSorted = atom(
	null,
	async (get, set, input: CreatePageSortedInput): Promise<PageMetadata> => {
		const { comparator, ...createPageInput } = input;

		// For collab mode, we need to calculate the correct pageIndex for sorted insertion
		// since the server just inserts at the given index
		if (createPageInput.pageIndex === undefined && createPageInput.pageMetadata) {
			const metadataKey = metadataKeyForDocsRoute(createPageInput.documentId);
			const currentMetadata = get($documentMetadata(metadataKey));
			const existingPages = currentMetadata?.pagesMetadata ?? [];

			// Create a temporary page metadata object to use for comparison
			const tempPageMetadata = buildPageMetadata({
				pageId: createPageInput.pageMetadata.id ?? "",
				pageType: createPageInput.pageType,
				pageMetadata: createPageInput.pageMetadata,
			});

			// Find the correct index by sorting and finding where the new page would go
			const sortedWithNew = [...existingPages, tempPageMetadata].sort(comparator);
			const pageIndex = sortedWithNew.findIndex((p) => p.id === tempPageMetadata.id);

			createPageInput.pageIndex = pageIndex >= 0 ? pageIndex : existingPages.length;
		}

		return createPageWithStrategy(get, set, createPageInput, (existingPages, newPage) =>
			[...existingPages, newPage].sort(comparator)
		);
	}
);

async function createMarkdownPage(get: Getter, set: Setter, input: CreatePageInput) {
	const { documentId, pageMetadata: inputPageMetadata, initialPageContents } = input;
	const metadataKey = metadataKeyForDocsRoute(documentId);
	const currentMetadata = get($documentMetadata(metadataKey));
	const existingPages = currentMetadata?.pagesMetadata ?? [];

	// Generate page ID and metadata
	const pageId = inputPageMetadata?.id ?? generateShortUuid();

	// Derive path from storagePath if not explicitly provided
	const pagePath =
		input.path ?? (input.storagePath ? `${input.storagePath}/index.md` : undefined);

	const pageMetadata: PageMetadata = {
		...buildPageMetadata({
			pageId,
			pageType: input.pageType,
			pageMetadata: inputPageMetadata,
		}),
		...(pagePath && { path: pagePath }),
	};

	const document = get($document(documentId));
	if (!document) {
		throw new CreatePageDocumentNotFoundError(documentId);
	}

	const remote = docsListItemRemoteEntry(document);
	const collab = docsListCollabEntry(remote);
	const local = docsListItemLocalEntry(document);

	// Parse body content if provided, otherwise create empty document
	let initialDocContents: Node;
	if (initialPageContents) {
		const { doc: parsedDoc } = parsePage(initialPageContents);
		initialDocContents = Node.fromJSON(schema, parsedDoc);
	} else {
		initialDocContents = Node.fromJSON(schema, {
			type: "doc",
			content: [{ type: "paragraph", content: [] }],
		});
	}

	// Register new page with the collab registry.
	if (collab) {
		logger.debug("CreatePage collab workflow");

		if (!remote) {
			throw new CreatePageCollaDocumentMetadataError(documentId);
		}

		const workspace = remote.workspace;
		const reg = get($collabRegistryConnection(workspace.slug))!;
		if (!reg) {
			throw new CreatePageCollabNoRegistryConnectionError(workspace.slug);
		}

		const abort = new AbortController();
		setTimeout(() => abort.abort(), 15000);

		logger.debug("Creating new page in collab registry", { documentId, pageMetadata });

		const result = await reg.registry.createNewPage(
			reg.context,
			{
				documentId,
				branch: collab.branch,
				sha: collab.sha,
				pageIndex: input.pageIndex,
				pageMetadataJson: Value.fromJson(JSON.stringify(pageMetadata)),
				pageProsemirrorJson: Struct.fromJson(
					serializeForStruct(initialDocContents.toJSON())
				),
			},
			{ signal: abort.signal }
		);
		logger.debug("Page created in collab registry", { documentId, pageMetadata, result });
	}

	// Update on-disk if the user has the document locally.
	if (local) {
		logger.debug("Updating filesystem");
		const fsApi = fs();
		const pathsApi = paths();

		let pageFilePath: string;

		// If path is explicitly provided in metadata, use it directly (for flat file structures)
		if (pageMetadata.path) {
			pageFilePath = `${local.path}/${pageMetadata.path}`;
			// Ensure the parent directory exists
			const parentDir = pageFilePath.substring(0, pageFilePath.lastIndexOf("/"));
			await set(fsApi.mkdir, { dirPath: parentDir });
			logger.debug("Parent directory ensured", { parentDir });
		} else {
			// Create page directory - use explicit storagePath if provided
			let pageDirPath: string;
			let dirName: string | undefined;
			if (input.storagePath) {
				pageDirPath = `${local.path}/${input.storagePath}`;
				pageMetadata.path = `${input.storagePath}/index.md`;
			} else {
				const hasExplicitSlugMetadata =
					inputPageMetadata?.slug !== undefined ||
					inputPageMetadata?.parentPageID !== undefined;
				if (hasExplicitSlugMetadata) {
					const derivedPath = buildDefaultPagePath(
						[...existingPages, pageMetadata],
						pageMetadata
					);
					pageMetadata.path = derivedPath;
					const lastSlash = derivedPath.lastIndexOf("/");
					const dirRelative =
						lastSlash >= 0 ? derivedPath.slice(0, lastSlash) : derivedPath;
					pageDirPath = `${local.path}/${dirRelative}`;
				} else {
					// Use slugified title as directory name when a title is provided
					const title = inputPageMetadata?.title;
					const slug = title ? sluggify(title) : "";
					if (slug !== "") {
						const resolvedDir = await resolveUniqueSlugPageDir(
							get,
							fsApi,
							local.path,
							pageId,
							slug
						);
						dirName = resolvedDir.dirName;
						pageDirPath = resolvedDir.pageDirPath;
					} else {
						pageDirPath = await get(pathsApi.getPageDirPath({ documentId, pageId }));
					}
				}
			}
			await set(fsApi.mkdir, { dirPath: pageDirPath });
			logger.debug("Page directory created", { pageDirPath });

			// Create page file (index.md)
			pageFilePath = pageMetadata.path
				? `${local.path}/${pageMetadata.path}`
				: `${pageDirPath}/index.md`;

			// Set the path in metadata so loading/saving uses the correct location
			if (dirName) {
				pageMetadata.path = `pages/${dirName}/index.md`;
			}
		}

		logger.debug("create page", { input });

		const initialDocMdContents = serializePage(initialDocContents);
		await set(fsApi.writeTextFile, {
			filePath: pageFilePath,
			content: initialDocMdContents,
			documentId,
		});
		logger.debug("Page file created", { pageFilePath });
	}

	return { pageMetadata, collabUsed: !!collab };
}

async function createEmptyCanvasPage(_get: Getter, _set: Setter, input: CreatePageInput) {
	const { pageMetadata: inputPageMetadata } = input;

	const pageId = inputPageMetadata?.id ?? generateShortUuid();
	const pageMetadata = buildPageMetadata({
		pageId,
		pageType: input.pageType,
		pageMetadata: inputPageMetadata,
	});

	return { pageMetadata, collabUsed: false };
}

async function createRequestPage(get: Getter, set: Setter, input: CreatePageInput) {
	const { documentId, pageMetadata: inputPageMetadata } = input;

	// Block creating request pages in published documents
	const isPublished = await get($isDocumentPublished(documentId));
	if (isPublished) {
		throw new Error("Cannot create request pages in published documents");
	}

	const pageId = inputPageMetadata?.id ?? generateShortUuid();
	const pageMetadata = buildPageMetadata({
		pageId,
		pageType: "request",
		pageMetadata: inputPageMetadata,
	});

	const document = get($document(documentId));
	if (!document) {
		throw new CreatePageDocumentNotFoundError(documentId);
	}

	const remote = docsListItemRemoteEntry(document);
	const collab = docsListCollabEntry(remote);
	const local = docsListItemLocalEntry(document);

	const requestPageContent = input.initialRequestPageContents ?? getDefaultHttpRequestPage();

	// Register new page with the collab registry.
	if (collab) {
		logger.debug("CreatePage request collab workflow");

		if (!remote) {
			throw new CreatePageCollaDocumentMetadataError(documentId);
		}

		const workspace = remote.workspace;
		const reg = get($collabRegistryConnection(workspace.slug))!;
		if (!reg) {
			throw new CreatePageCollabNoRegistryConnectionError(workspace.slug);
		}

		const abort = new AbortController();
		setTimeout(() => abort.abort(), 15000);

		logger.debug("Creating new request page in collab registry", { documentId, pageMetadata });

		// Request pages need an empty prosemirror doc and the requestPageJson field
		const result = await reg.registry.createNewPage(
			reg.context,
			{
				documentId,
				branch: collab.branch,
				sha: collab.sha,
				pageIndex: input.pageIndex,
				pageMetadataJson: Value.fromJson(JSON.stringify(pageMetadata)),
				pageProsemirrorJson: Struct.fromJson(serializeForStruct(EMPTY_DOC_JSON)),
				requestPageJson: Value.fromJson(requestPageContent),
			},
			{ signal: abort.signal }
		);
		logger.debug("Request page created in collab registry", {
			documentId,
			pageMetadata,
			result,
		});
	}

	// Update on-disk if the user has the document locally.
	if (local) {
		logger.debug("Updating filesystem for request page");
		const fsApi = fs();

		// Use the actual document path from the local entry (same as the loader uses)
		const documentPath = local.path;

		// Keep request pages aligned with explicit metadata paths so filesystem actions
		// and git/diff UI can resolve them the same way as markdown pages.
		const title = inputPageMetadata?.title;
		const slug = title ? sluggify(title) : "";
		let dirName = pageId;
		let pageDirPath: string;

		if (slug !== "") {
			const resolvedDir = await resolveUniqueSlugPageDir(
				get,
				fsApi,
				documentPath,
				pageId,
				slug
			);
			dirName = resolvedDir.dirName;
			pageDirPath = resolvedDir.pageDirPath;
		} else {
			pageDirPath = await getPageDirPath(documentPath, pageId);
		}
		await set(fsApi.mkdir, { dirPath: pageDirPath });
		logger.debug("Request page directory created", { pageDirPath });

		// Create page file (index.yml for request pages)
		const pageFilePath = await get(fsApi.pathJoin([pageDirPath, "index.yml"]));
		await set(fsApi.writeYamlFile, {
			filePath: pageFilePath,
			content: requestPageContent,
			documentId,
		});
		logger.debug("Request page file created", { pageFilePath });

		pageMetadata.path = `pages/${dirName}/index.yml`;

		// Eagerly load the request page into the atom with the correct content.
		// This prevents a race condition where $documentLoaderEffect fires before
		// the file is visible on disk, falling back to HTTP defaults.
		const requestPageKey = requestPageKeyForDocsRoute({ documentId, pageId });
		set($loadHttpRequestPage, {
			key: requestPageKey,
			record: {
				source: "disk",
				request: requestPageContent,
				importEpoch: 0,
			},
			ssr: false,
			onPersist: async (requestData) => {
				await set(fsApi.writeYamlFile, {
					filePath: pageFilePath,
					content: requestData,
					documentId,
				});
			},
		});
		logger.debug("Request page eagerly loaded into atom", { requestPageKey });
	}

	return { pageMetadata, collabUsed: !!collab };
}
